import { Component, ReactNode } from "react";
import { Button, Result } from "antd";

interface ErrorBoundaryState {
  readonly hasError: boolean;
  readonly error?: Error;
}

interface ErrorBoundaryProps {
  readonly children: ReactNode;
}

// App-level error boundary. If a page throws during render we show a graceful
// fallback instead of a white screen, letting the user reload without losing
// the session.
export default class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: unknown) {
    // Surface to console for debugging — in prod wire this to a logger if needed.
    // eslint-disable-next-line no-console
    console.error("App crashed:", error, info);
  }

  reset = () => {
    this.setState({ hasError: false, error: undefined });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Result
          status="error"
          title="Something went wrong"
          subTitle={
            this.state.error?.message ??
            "The page couldn't finish loading. Reload to try again."
          }
          extra={
            <Button type="primary" onClick={this.reset}>
              Reload
            </Button>
          }
        />
      );
    }
    return this.props.children;
  }
}
